import { useCallback, useState } from 'react';
import { Button, Input, Modal, Select, Space, Steps, Table, Tag, Typography, message } from 'antd';
import type { DictionaryColumn, DictionaryEntry } from '@lens/shared';
import { RemoteSourcePanel } from '../../shared/import/RemoteSourcePanel.tsx';
import { matchHeaders, readWorkbook, resolveSheetName, sheetToHeadersRows } from '../../shared/import/workbook.ts';
import { radius, spacing } from '../../shared/theme/tokens.ts';
import { createEntry, listEntries, updateEntry } from './api.ts';

const { Text } = Typography;

interface Props {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
  dictId: string;
  columns: DictionaryColumn[];
  keyColumnId: string | null;
}

interface ColMapping {
  csvHeader: string;
  colSlug: string | null;
}

interface ImportResult {
  created: number;
  updated: number;
  errors: Array<{ row: number; message: string; data: string }>;
}

/**
 * Единый парсер для .csv / .xlsx / .xls через SheetJS — нужен, чтобы
 * Google Sheets (отдаются прокси как XLSX) и локальный CSV проходили
 * один и тот же путь. `sheetName` — выбранная в UI вкладка (для книг с
 * несколькими листами); если не передана — берётся первый лист.
 */
async function parseSpreadsheet(
  file: File,
  sheetName?: string,
): Promise<{ headers: string[]; rows: string[][] }> {
  const wb = await readWorkbook(file);
  const name = resolveSheetName(wb, sheetName);
  if (!name) return { headers: [], rows: [] };
  return sheetToHeadersRows(wb, name);
}

function autoMap(headers: string[], columns: DictionaryColumn[]): ColMapping[] {
  const matched = matchHeaders(headers, columns, (c) => [c.slug, c.name]);
  return headers.map((header) => ({
    csvHeader: header,
    colSlug: matched.get(header)?.slug ?? null,
  }));
}

async function loadAllEntries(dictId: string): Promise<DictionaryEntry[]> {
  const all: DictionaryEntry[] = [];
  let cursor: string | undefined;
  do {
    const resp = await listEntries(dictId, { cursor, limit: 500 });
    all.push(...resp.items);
    cursor = resp.nextCursor ?? undefined;
  } while (cursor);
  return all;
}

export function DictCsvImportModal({ open, onClose, onDone, dictId, columns, keyColumnId }: Props) {
  const [step, setStep] = useState(0);
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [previewRows, setPreviewRows] = useState<string[][]>([]);
  const [allRows, setAllRows] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<ColMapping[]>([]);
  const [upsert, setUpsert] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [errorSearch, setErrorSearch] = useState('');

  const keyColumn = columns.find((c) => c.id === keyColumnId);

  const handleFile = useCallback(
    (file: File, sheetName: string): void => {
      void parseSpreadsheet(file, sheetName)
        .then(({ headers: h, rows }) => {
          if (h.length === 0 || rows.length === 0) {
            void message.warning('Файл пустой или содержит только заголовок');
            return;
          }
          setFileName(file.name);
          setHeaders(h);
          setAllRows(rows);
          setPreviewRows(rows.slice(0, 10));
          setMapping(autoMap(h, columns));
          setStep(1);
        })
        .catch((err) => {
          void message.error(
            err instanceof Error
              ? `Не удалось прочитать файл: ${err.message}`
              : 'Не удалось прочитать файл',
          );
        });
    },
    [columns],
  );

  const handleImport = useCallback(async () => {
    setLoading(true);
    const res: ImportResult = { created: 0, updated: 0, errors: [] };
    try {
      const existingByKey = new Map<string, string>();
      if (upsert && keyColumn) {
        const entries = await loadAllEntries(dictId);
        for (const e of entries) {
          const val = String(e.values[keyColumn.slug] ?? '').toLowerCase();
          if (val) existingByKey.set(val, e.id);
        }
      }

      for (let i = 0; i < allRows.length; i++) {
        const row = allRows[i]!;
        const values: Record<string, unknown> = {};
        for (const { csvHeader, colSlug } of mapping) {
          if (!colSlug) continue;
          const csvIdx = headers.indexOf(csvHeader);
          const raw = row[csvIdx] ?? '';
          const col = columns.find((c) => c.slug === colSlug);
          if (!col) continue;
          if (col.columnType === 'number') {
            const n = Number(raw);
            values[colSlug] = raw && !Number.isNaN(n) ? n : undefined;
          } else if (col.columnType === 'boolean') {
            values[colSlug] = ['true', 'да', '1', 'yes'].includes(raw.toLowerCase());
          } else {
            values[colSlug] = raw || undefined;
          }
        }
        try {
          let matched: string | undefined;
          if (upsert && keyColumn) {
            const keyVal = String(values[keyColumn.slug] ?? '').toLowerCase();
            matched = keyVal ? existingByKey.get(keyVal) : undefined;
          }
          if (matched) {
            await updateEntry(dictId, matched, { values });
            res.updated++;
          } else {
            await createEntry(dictId, { values });
            res.created++;
          }
        } catch (e) {
          res.errors.push({
            row: i + 2,
            message: e instanceof Error ? e.message : 'Ошибка',
            data: row.join(' | '),
          });
        }
      }
    } catch (e) {
      void message.error(e instanceof Error ? e.message : 'Ошибка импорта');
    }
    setResult(res);
    setStep(2);
    setLoading(false);
  }, [allRows, columns, dictId, headers, keyColumn, mapping, upsert]);

  const handleClose = useCallback(() => {
    setStep(0);
    setFileName('');
    setHeaders([]);
    setAllRows([]);
    setPreviewRows([]);
    setMapping([]);
    setResult(null);
    setErrorSearch('');
    onClose();
  }, [onClose]);

  const handleDone = useCallback(() => {
    handleClose();
    onDone();
  }, [handleClose, onDone]);

  const mappedCount = mapping.filter((m) => m.colSlug).length;

  const mappingTableCols = [
    {
      title: 'Колонка в файле',
      dataIndex: 'csvHeader',
      key: 'csvHeader',
      render: (v: string) => <Text strong>{v}</Text>,
    },
    {
      title: '',
      key: 'arrow',
      width: 40,
      render: () => <Text type="secondary">→</Text>,
    },
    {
      title: 'Колонка справочника',
      key: 'colSlug',
      render: (_: unknown, record: ColMapping) => (
        <Select
          style={{ width: 200 }}
          value={record.colSlug ?? '__skip__'}
          onChange={(val) =>
            setMapping((prev) =>
              prev.map((m) =>
                m.csvHeader === record.csvHeader
                  ? { ...m, colSlug: val === '__skip__' ? null : val }
                  : m,
              ),
            )
          }
        >
          <Select.Option value="__skip__">— не импортировать —</Select.Option>
          {columns.map((c) => (
            <Select.Option key={c.slug} value={c.slug}>
              {c.name}
              {c.id === keyColumnId && ' 🔑'}
            </Select.Option>
          ))}
        </Select>
      ),
    },
    {
      title: 'Пример',
      key: 'preview',
      render: (_: unknown, record: ColMapping) => {
        const idx = headers.indexOf(record.csvHeader);
        const val = previewRows[0]?.[idx] ?? '';
        return <Text type="secondary">{val || '—'}</Text>;
      },
    },
  ];

  const previewTableCols = [
    { title: '#', key: 'idx', width: 40, render: (_: unknown, __: unknown, i: number) => i + 1 },
    ...mapping
      .filter((m) => m.colSlug)
      .map((m) => ({
        title: `${m.csvHeader} → ${columns.find((c) => c.slug === m.colSlug)?.name ?? m.colSlug}`,
        key: m.csvHeader,
        render: (_: unknown, row: string[]) => {
          const idx = headers.indexOf(m.csvHeader);
          return row[idx] ?? '—';
        },
      })),
  ];

  const filteredErrors = (result?.errors ?? []).filter((error) =>
    error.message.toLowerCase().includes(errorSearch.trim().toLowerCase()),
  );

  const errorColumns = [
    {
      title: 'Строка',
      dataIndex: 'row',
      key: 'row',
      sorter: (a: { row: number }, b: { row: number }) => a.row - b.row,
      defaultSortOrder: 'ascend' as const,
      width: 110,
    },
    {
      title: 'Ошибка',
      dataIndex: 'message',
      key: 'message',
      sorter: (a: { message: string }, b: { message: string }) => a.message.localeCompare(b.message, 'ru'),
    },
    {
      title: 'Данные',
      dataIndex: 'data',
      key: 'data',
      sorter: (a: { data: string }, b: { data: string }) => a.data.localeCompare(b.data, 'ru'),
      render: (value: string) => (
        <Text ellipsis={{ tooltip: value }} style={{ display: 'block', maxWidth: 320 }}>
          {value || '—'}
        </Text>
      ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title="Импорт CSV / Excel"
      width={800}
      footer={null}
      destroyOnClose
    >
      <Steps
        current={step}
        size="small"
        style={{ marginBottom: spacing.lg }}
        items={[
          { title: 'Загрузка файла' },
          { title: 'Маппинг колонок' },
          { title: 'Результат' },
        ]}
      />

      {/* Step 0 — file upload */}
      {step === 0 && (
        <RemoteSourcePanel
          accept=".csv,.tsv,.txt,.xlsx,.xls"
          formatsHint="Поддерживается .csv, .xlsx, .xls (запятая / ; / табуляция)"
          onLoaded={handleFile}
        />
      )}

      {/* Step 1 — mapping */}
      {step === 1 && (
        <Space direction="vertical" style={{ width: '100%' }} size={spacing.base}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary">📄 {fileName} · {allRows.length} строк</Text>
            <Tag
              style={
                mappedCount > 0
                  ? { background: 'var(--color-success-bg)', color: 'var(--color-success)', border: 'none' }
                  : { background: 'var(--color-warning-bg)', color: 'var(--color-warning)', border: 'none' }
              }
            >
              {mappedCount} из {headers.length} сопоставлено
            </Tag>
          </div>

          <Table
            dataSource={mapping}
            columns={mappingTableCols}
            rowKey="csvHeader"
            pagination={false}
            size="small"
            bordered
          />

          {previewRows.length > 0 && (
            <>
              <Text type="secondary" style={{ fontWeight: 500 }}>Превью (первые {previewRows.length} строк)</Text>
              <Table
                dataSource={previewRows}
                columns={previewTableCols}
                rowKey={(_, i) => String(i)}
                pagination={false}
                size="small"
                scroll={{ x: true, y: 240 }}
              />
            </>
          )}

          <div style={{ padding: `${spacing.md}px ${spacing.base}px`, background: 'var(--color-bg-layout)', borderRadius: radius.md, border: '1px solid var(--color-border)' }}>
            <Text strong style={{ display: 'block', marginBottom: spacing.sm }}>Режим импорта</Text>
            <Space direction="vertical">
              <label style={{ cursor: 'pointer', display: 'flex', gap: spacing.sm, alignItems: 'flex-start' }}>
                <input type="radio" checked={upsert} onChange={() => setUpsert(true)} style={{ marginTop: spacing.xs }} />
                <span>
                  <Text strong>Upsert по ключевой колонке{keyColumn ? ` (${keyColumn.name})` : ''}</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>Если запись с таким ключом уже существует — обновить, иначе создать</Text>
                </span>
              </label>
              <label style={{ cursor: 'pointer', display: 'flex', gap: spacing.sm, alignItems: 'flex-start' }}>
                <input type="radio" checked={!upsert} onChange={() => setUpsert(false)} style={{ marginTop: spacing.xs }} />
                <span>
                  <Text strong>Только создавать новые</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>Дубликаты по ключу будут пропущены с ошибкой</Text>
                </span>
              </label>
            </Space>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: spacing.sm }}>
            <Button onClick={() => setStep(0)}>← Назад</Button>
            <Button
              type="primary"
              loading={loading}
              disabled={mappedCount === 0}
              onClick={() => void handleImport()}
            >
              Импортировать {allRows.length} записей →
            </Button>
          </div>
        </Space>
      )}

      {/* Step 2 — result */}
      {step === 2 && result && (
        <Space direction="vertical" style={{ width: '100%' }} size={spacing.base}>
          <div style={{ display: 'flex', gap: spacing.base }}>
            <div style={{ flex: 1, textAlign: 'center', padding: spacing.base, background: 'var(--color-success-bg)', borderRadius: radius.md, border: `1px solid ${'var(--color-success)'}` }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--color-success)' }}>{result.created}</div>
              <div style={{ color: 'var(--color-text-secondary)', fontSize: 12, marginTop: spacing.xs }}>Создано</div>
            </div>
            <div style={{ flex: 1, textAlign: 'center', padding: spacing.base, background: 'var(--color-info-bg)', borderRadius: radius.md, border: `1px solid ${'var(--color-info)'}` }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--color-info)' }}>{result.updated}</div>
              <div style={{ color: 'var(--color-text-secondary)', fontSize: 12, marginTop: spacing.xs }}>Обновлено</div>
            </div>
            <div style={{ flex: 1, textAlign: 'center', padding: spacing.base, background: result.errors.length ? 'var(--color-error-bg)' : 'var(--color-bg-layout)', borderRadius: radius.md, border: `1px solid ${result.errors.length ? 'var(--color-error)' : 'var(--color-border)'}` }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: result.errors.length ? 'var(--color-error)' : 'var(--color-text-secondary)' }}>{result.errors.length}</div>
              <div style={{ color: 'var(--color-text-secondary)', fontSize: 12, marginTop: spacing.xs }}>Ошибок</div>
            </div>
          </div>

          {result.errors.length > 0 && (
            <Space direction="vertical" style={{ width: '100%' }} size={spacing.sm}>
              <Input.Search
                placeholder="Поиск по ошибкам..."
                allowClear
                value={errorSearch}
                onChange={(e) => setErrorSearch(e.target.value)}
              />
              <Table
                dataSource={filteredErrors}
                columns={errorColumns}
                rowKey={(record) => `${record.row}-${record.message}-${record.data}`}
                size="small"
                pagination={{ pageSize: 8, hideOnSinglePage: true }}
                scroll={{ y: 240 }}
              />
            </Space>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: spacing.sm }}>
            <Button onClick={() => { setStep(0); setResult(null); }}>Импортировать ещё</Button>
            <Button type="primary" onClick={handleDone}>← К справочнику</Button>
          </div>
        </Space>
      )}
    </Modal>
  );
}
